// ============================================================================
// MAIN GAME CLASS - Orchestrates all systems
// ============================================================================

import { GameLoop, InputManager, Renderer } from '@/engine';
import { NetworkManager } from '@/network';
import { GameWorld } from '@/game/GameWorld';
import { GameRenderer } from '@/game/GameRenderer';
import { GameAudio, getGameAudio, SFX } from '@/game/GameAudio';
import { BotControllerFactory, BotAIMode } from '@/game/ai/BotControllerFactory';
import { 
  GameMode, 
  GamePhase, 
  Team, 
  HeroId, 
  PacketType, 
  PlayerInput,
  Packet,
  GameEventType,
  GameState,
  PlayerState,
  ProjectileState,
  ObjectiveState,
  EntityId,
} from '@/shared/types';
import { NETWORK, HEROES } from '@/shared/constants';
import { DEFAULT_CONFIG } from '@/shared/types';
import { FIREBASE_CONFIG } from '@/config/firebase.config';
import { PlayerStatsService } from '@/services/PlayerStatsService';
import { AnalyticsService } from '@/services/AnalyticsService';

export interface GameSettings {
  enemyBotCount: number;
  friendlyBotCount: number;
  botDifficulty: 'easy' | 'medium' | 'hard';
  gameMode: 'deathmatch' | 'spike';
  mapId: string;
  playerHero: string;
  abilityQSlot: 1 | 2;  // Which ability (1 or 2) is assigned to Q key
  abilityFSlot: 1 | 2;  // Which ability (1 or 2) is assigned to F key
  totalRounds: number;  // Best of X (3, 5, 7, 9, 13)
  aiMode?: 'rule_based' | 'slm';  // Bot AI mode (optional, defaults to rule_based)
}

export interface LobbyPlayer {
  id: string;  // peerId
  name: string;
  isHost: boolean;
}

export interface GameOptions {
  canvas: HTMLCanvasElement;
  playerName: string;
  settings?: GameSettings;
  network?: NetworkManager;  // Existing network connection from lobby
  lobbyPlayers?: LobbyPlayer[];  // Players from lobby with their names
  appVersion?: string;  // Application version
}

// JSON-serializable version of GameState for network transmission
interface SerializedGameState {
  phase: GamePhase;
  mode: GameMode;
  mapId: string;
  tick: number;
  time: number;
  roundStartTime: number;
  roundNumber: number;
  maxRounds: number;
  roundTimeLimit: number;
  attackerScore: number;
  defenderScore: number;
  players: Array<[EntityId, PlayerState]>;
  projectiles: Array<[EntityId, ProjectileState]>;
  objectives: ObjectiveState;
  recentShots?: Array<{
    start: { x: number; y: number };
    end: { x: number; y: number };
    shooterId: EntityId;
    shooterPeerId: string;
  }>;
  countdownTime?: number;
  countdownPaused?: boolean;
  countdownPausedBy?: string;
  // Game pause state (tab focus sync)
  gamePaused?: boolean;
  gamePausedBy?: string;
  // Disconnect forfeit state
  disconnectForfeit?: boolean;
  disconnectedPlayerName?: string;
  // Supply drop system
  aircraft?: Array<[EntityId, import('./entities/Aircraft').AircraftState]>;
  supplyCrates?: Array<[EntityId, import('./entities/SupplyCrate').SupplyCrateState]>;
  bombs?: Array<[EntityId, import('./entities/Bomb').BombState]>;
  grenades?: Array<[EntityId, import('./entities/Grenade').GrenadeState]>;
}

const DEFAULT_SETTINGS: GameSettings = {
  enemyBotCount: 3,
  friendlyBotCount: 0,
  botDifficulty: 'medium',
  gameMode: 'deathmatch',
  playerHero: 'vanta',
  abilityQSlot: 1,
  abilityFSlot: 2,
  totalRounds: 5,
  mapId: 'neon_arena',
};

export class Game {
  // Core systems
  private gameLoop: GameLoop;
  private inputManager: InputManager;
  private renderer: Renderer;
  private audio: GameAudio;

  // Game state
  private world: GameWorld;
  private gameRenderer: GameRenderer;

  // Networking
  private network: NetworkManager | null = null;
  private existingNetwork: NetworkManager | null = null;  // From lobby
  private lobbyPlayers: LobbyPlayer[] = [];  // Players from lobby
  private isHost = false;
  private localPeerId = '';
  private debugBroadcastStarted = false;  // Debug flag for first broadcast

  // Config
  private playerName: string;
  private canvas: HTMLCanvasElement;
  private settings: GameSettings;

  // State
  private initialized = false;
  private lastInputSendTime = 0;
  private lastPingTime = 0;
  private lastLocalFireTime = 0; // Track local player shooting for muzzle flash
  private lastStateBroadcastTime = 0; // Track state broadcast for network sync

  // Debug
  private debugInfo: HTMLElement | null = null;
  private debugLoggedNoPlayer = false; // Only log missing player warning once
  private debugReceivedState = false; // Track if we've received state from host
  
  // Show FPS/Debug info (controlled from settings)
  private showFPS = true;
  
  // Phase tracking for audio
  private lastPhase: GamePhase = GamePhase.RoundActive;
  private countdownSoundId: string | null = null; // Track countdown sound for pause/resume
  private lastCountdownPaused: boolean = false; // Track pause state changes

  // Hero tutorial system
  private heroTutorialShown = false;
  private heroTutorialOverlay: HTMLElement | null = null;

  constructor(options: GameOptions) {
    this.canvas = options.canvas;
    this.playerName = options.playerName;
    this.settings = options.settings || DEFAULT_SETTINGS;
    this.existingNetwork = options.network || null;  // Accept existing network from lobby
    this.lobbyPlayers = options.lobbyPlayers || [];  // Store lobby players
    
    console.log(`[Game] Constructor - existingNetwork: ${!!this.existingNetwork}, lobbyPlayers: ${this.lobbyPlayers.length}`);
    console.log(`[Game] Ability settings - Q slot: ${this.settings.abilityQSlot}, F slot: ${this.settings.abilityFSlot}`);
    if (this.existingNetwork) {
      console.log(`[Game] Constructor - existing network has ${this.existingNetwork.getPeerIds().length} peers`);
    }
    
    // Set bot AI mode from settings
    if (this.settings.aiMode === 'slm') {
      BotControllerFactory.setMode(BotAIMode.SLM);
    } else {
      BotControllerFactory.setMode(BotAIMode.RuleBased);
    }
    console.log(`[Game] Bot AI mode: ${BotControllerFactory.getModeName()}`);

    // Initialize renderer
    this.renderer = new Renderer(
      this.canvas,
      DEFAULT_CONFIG.canvasWidth,
      DEFAULT_CONFIG.canvasHeight
    );

    // Initialize input
    this.inputManager = new InputManager(this.canvas);

    // Initialize audio (singleton)
    this.audio = getGameAudio();

    // Determine game mode from settings
    const gameMode = this.settings.gameMode === 'spike' 
      ? GameMode.DataSpikeAssault 
      : GameMode.NeonDeathmatch;

    // Initialize game world
    this.world = new GameWorld({
      mode: gameMode,
      maxPlayers: 8,
      isHost: true, // Start as host, change when joining
      abilityQSlot: this.settings.abilityQSlot,
      abilityFSlot: this.settings.abilityFSlot,
      totalRounds: this.settings.totalRounds,
      mapId: this.settings.mapId,
    });

    // Initialize game renderer
    const version = options.appVersion || '0.0.0';
    this.gameRenderer = new GameRenderer(this.renderer, this.world, version);

    // Initialize game loop
    this.gameLoop = new GameLoop(
      DEFAULT_CONFIG.tickRate,
      this.update.bind(this),
      this.render.bind(this)
    );

    // Debug
    this.debugInfo = document.getElementById('debug-info');
  }

  async init(): Promise<void> {
    if (this.initialized) return;

    // Initialize audio and load essential sounds
    await this.audio.init();
    await this.audio.loadEssentialSounds();

    // Load assets (placeholder for now)
    // await this.assetManager.loadAssets([...]);

    this.initialized = true;
    console.log('[Game] Initialized');
  }

  start(): void {
    if (!this.initialized) {
      console.error('[Game] Not initialized');
      return;
    }

    this.gameLoop.start();
    console.log('[Game] Started');
    
    // Show hero tutorial on first game with this hero
    this.checkAndShowHeroTutorial();
  }

  stop(): void {
    // Stop countdown sound if playing
    if (this.countdownSoundId) {
      this.audio.stop(this.countdownSoundId);
      this.countdownSoundId = null;
    }
    this.gameLoop.stop();
    console.log('[Game] Stopped');
  }

  pause(): void {
    this.gameLoop.stop();
    console.log('[Game] Paused');
  }

  resume(): void {
    this.gameLoop.start();
    console.log('[Game] Resumed');
  }

  isPaused(): boolean {
    return !this.gameLoop.isRunning();
  }

  restartGame(): void {
    if (!this.isHost) return;
    
    // Determine game mode from settings
    const gameMode = this.settings.gameMode === 'spike' 
      ? GameMode.DataSpikeAssault 
      : GameMode.NeonDeathmatch;
    
    // Create fresh world
    this.world = new GameWorld({
      mode: gameMode,
      maxPlayers: 8,
      isHost: true,
      abilityQSlot: this.settings.abilityQSlot,
      abilityFSlot: this.settings.abilityFSlot,
      totalRounds: this.settings.totalRounds,
      mapId: this.settings.mapId,
    });
    this.gameRenderer.updateWorld(this.world);

    // Get hero from settings
    const heroId = this.getHeroFromSettings();

    // Re-add local player with correct peerId
    const player = this.world.addPlayer(
      this.localPeerId || 'local',
      this.playerName,
      Team.Attackers,
      heroId
    );
    this.world.setLocalPlayer(player.id);
    
    // Re-add connected remote players (multiplayer mode)
    // Check if we have actual connected peers (not just a network for room display)
    const connectedPeers = this.network?.getPeerCount() || 0;
    const isMultiplayerGame = this.network !== null && connectedPeers > 0;
    
    if (isMultiplayerGame) {
      const peerIds = this.network!.getPeerIds();
      console.log(`[Game] Restart: Re-adding ${peerIds.length} connected peers to Defenders`);
      for (const peerId of peerIds) {
        // Try to find name from lobby players
        const lobbyPlayer = this.lobbyPlayers.find(p => p.id === peerId);
        const playerName = lobbyPlayer?.name || `Player ${peerId.slice(-4)}`;
        this.world.addPlayer(peerId, playerName, Team.Defenders, HeroId.Vanta);
      }
      // In multiplayer, never spawn bots
    } else {
      // No connected peers - this is single player, add bots
      const botCount = this.settings.enemyBotCount;
      const difficulty = this.settings.botDifficulty;
      if (botCount > 0) {
        this.world.spawnBots(botCount, Team.Defenders, difficulty);
        console.log(`[Game] Restart: Spawning ${botCount} enemy bots for single player`);
      }
      // Add friendly bots to player's team (Attackers)
      const friendlyBotCount = this.settings.friendlyBotCount;
      if (friendlyBotCount > 0) {
        this.world.spawnBots(friendlyBotCount, Team.Attackers, difficulty);
        console.log(`[Game] Restart: Spawning ${friendlyBotCount} friendly bots`);
      }
    }

    // Start the round
    this.world.setPhase(GamePhase.RoundActive);
    this.world.startRound();

    console.log('[Game] Restarted');
  }

  nextRound(): void {
    if (!this.isHost) return;
    
    // Start the next round without resetting scores
    this.world.setPhase(GamePhase.RoundActive);
    this.world.startRound();

    console.log('[Game] Next round started');
  }

  // Get round end info for UI
  getRoundEndInfo(): { 
    isRoundOver: boolean; 
    isMatchOver: boolean;
    winningTeam: 'attackers' | 'defenders'; 
    attackerScore: number; 
    defenderScore: number;
    roundsToWin: number;
    totalRounds: number;
    isForfeit: boolean;
    forfeitPlayerName: string;
  } | null {
    const phase = this.world.getPhase();
    if (phase !== GamePhase.RoundEnd && phase !== GamePhase.MatchEnd) {
      return null;
    }
    
    const attackerScore = this.world.getAttackerScore();
    const defenderScore = this.world.getDefenderScore();
    const winningTeam = attackerScore > defenderScore ? 'attackers' : 'defenders';
    
    return {
      isRoundOver: true,
      isMatchOver: phase === GamePhase.MatchEnd,
      winningTeam,
      attackerScore,
      defenderScore,
      roundsToWin: this.world.getRoundsToWin(),
      totalRounds: this.world.getTotalRounds(),
      isForfeit: this.world.isDisconnectForfeit(),
      forfeitPlayerName: this.world.getDisconnectedPlayerName(),
    };
  }

  // Get countdown info for UI
  getCountdownInfo(): {
    countdownTime: number;
    isPaused: boolean;
    pausedBy: string;
  } | null {
    const phase = this.world.getPhase();
    if (phase !== GamePhase.RoundEnd) {
      return null;
    }
    
    return {
      countdownTime: this.world.getCountdownTime(),
      isPaused: this.world.isCountdownPaused(),
      pausedBy: this.world.getCountdownPausedBy(),
    };
  }

  // Check if round is currently active
  isRoundActive(): boolean {
    return this.world.getPhase() === GamePhase.RoundActive;
  }

  // Get local player's current stats
  getLocalPlayerStats(): { kills: number; deaths: number; assists: number; team: Team } | null {
    const localPlayer = this.world.getLocalPlayer();
    if (!localPlayer) return null;
    
    return {
      kills: localPlayer.kills,
      deaths: localPlayer.deaths,
      assists: localPlayer.assists,
      team: localPlayer.team,
    };
  }

  // Check if local player's team won the match
  didLocalPlayerWin(): boolean {
    const localPlayer = this.world.getLocalPlayer();
    if (!localPlayer) return false;
    
    const attackerScore = this.world.getAttackerScore();
    const defenderScore = this.world.getDefenderScore();
    
    if (localPlayer.team === Team.Attackers) {
      return attackerScore > defenderScore;
    } else {
      return defenderScore > attackerScore;
    }
  }

  // Track session start time for duration calculation
  private sessionStartTime: number = 0;
  private gamesPlayedThisSession: number = 0;

  // Start player stats session (call when game starts)
  async startStatsSession(): Promise<void> {
    this.sessionStartTime = Date.now();
    this.gamesPlayedThisSession = 0;
    
    try {
      await PlayerStatsService.startSession(this.playerName, this.settings.playerHero);
      console.log('[Game] Stats session started');
    } catch (e) {
      console.warn('[Game] Failed to start stats session:', e);
    }
    
    // Track in GA4
    AnalyticsService.trackHeroSelect(this.settings.playerHero);
  }

  // Record match result to player stats and GA4
  async recordMatchResult(): Promise<void> {
    const stats = this.getLocalPlayerStats();
    if (!stats) return;
    
    const won = this.didLocalPlayerWin();
    this.gamesPlayedThisSession++;
    
    // Record to Firebase
    try {
      await PlayerStatsService.recordGameResult(
        won,
        stats.kills,
        stats.deaths,
        stats.assists,
        this.settings.playerHero
      );
      console.log(`[Game] Match result recorded: ${won ? 'WIN' : 'LOSS'}`);
    } catch (e) {
      console.warn('[Game] Failed to record match result:', e);
    }
    
    // Track in GA4
    AnalyticsService.trackMatchEnd({
      gameMode: this.settings.gameMode,
      mapId: this.settings.mapId,
      won,
      durationSeconds: Math.floor((Date.now() - this.sessionStartTime) / 1000),
      kills: stats.kills,
      deaths: stats.deaths,
      assists: stats.assists,
      heroId: this.settings.playerHero,
    });
  }

  // End player stats session (call when quitting to menu)
  async endStatsSession(): Promise<void> {
    const durationSeconds = Math.floor((Date.now() - this.sessionStartTime) / 1000);
    
    // End Firebase session
    try {
      await PlayerStatsService.endSession();
      console.log('[Game] Stats session ended');
    } catch (e) {
      console.warn('[Game] Failed to end stats session:', e);
    }
    
    // Track in GA4
    AnalyticsService.trackGameEnd(durationSeconds, this.gamesPlayedThisSession);
  }

  // Synchronous version for page unload (saves to localStorage for later recovery)
  saveStatsOnUnload(): void {
    PlayerStatsService.saveUnsavedPlayTimeSync();
    console.log('[Game] Stats saved to localStorage for recovery');
  }

  // Track match start (call when match begins)
  trackMatchStart(): void {
    const playerCount = this.lobbyPlayers.length || 1;
    const botCount = this.settings.enemyBotCount + this.settings.friendlyBotCount;
    
    AnalyticsService.trackMatchStart({
      gameMode: this.settings.gameMode,
      mapId: this.settings.mapId,
      playerCount,
      botCount,
      isHost: this.isHost,
    });
  }

  // -------------------------------------------------------------------------
  // Networking
  // -------------------------------------------------------------------------

  async hostGame(roomCode?: string): Promise<string> {
    this.isHost = true;
    
    console.log(`[Game] hostGame() called - existingNetwork: ${!!this.existingNetwork}`);
    if (this.existingNetwork) {
      console.log(`[Game] hostGame() - existingNetwork peers: ${this.existingNetwork.getPeerIds().length}`);
    }
    
    // Determine game mode from settings
    const gameMode = this.settings.gameMode === 'spike' 
      ? GameMode.DataSpikeAssault 
      : GameMode.NeonDeathmatch;
    
    // Create new world as host
    this.world = new GameWorld({
      mode: gameMode,
      maxPlayers: 8,
      isHost: true,
      abilityQSlot: this.settings.abilityQSlot,
      abilityFSlot: this.settings.abilityFSlot,
      totalRounds: this.settings.totalRounds,
      mapId: this.settings.mapId,
    });
    this.gameRenderer.updateWorld(this.world);

    // Get hero from settings
    const heroId = this.getHeroFromSettings();

    // Use provided room code or generate one
    let code = roomCode || this.generateRoomCode();
    
    // IMPORTANT: Add local player FIRST before network setup
    // This ensures remote players go to the opposite team
    this.localPeerId = 'local'; // Temporary, will be updated if network connects
    
    // Add local player to Attackers first
    const player = this.world.addPlayer(
      'local',  // Temporary peerId
      this.playerName,
      Team.Attackers,
      heroId
    );
    this.world.setLocalPlayer(player.id);
    
    // Use existing network from lobby if available, otherwise create new
    if (this.existingNetwork) {
      console.log(`[Game] Using existing network from lobby`);
      this.network = this.existingNetwork;
      this.existingNetwork = null;
      
      // Debug: show peer info
      const peerIds = this.network.getPeerIds();
      const peerCount = this.network.getPeerCount();
      console.log(`[Game] Network has ${peerCount} peers: [${peerIds.join(', ')}]`);
      console.log(`[Game] Lobby players passed to game:`, this.lobbyPlayers);
      
      // Update local player's peerId to the real network peer ID
      const realPeerId = this.network.getLocalPeerId();
      this.localPeerId = realPeerId;
      player.peerId = realPeerId;
      this.world.updatePlayerPeerId('local', realPeerId);
      
      this.setupNetworkListeners();
      // Room already created in lobby, use that room code
      code = this.network.getRoomCode() || code;
      console.log(`[Game] Using existing room: ${code}`);
      
      // IMPORTANT: Add players for peers that are ALREADY connected from the lobby
      // These peers won't fire 'peerConnected' because they were connected before setupNetworkListeners
      for (const peerId of peerIds) {
        const lobbyPlayer = this.lobbyPlayers.find(p => p.id === peerId);
        const playerName = lobbyPlayer?.name || `Player ${peerId.slice(-4)}`;
        
        // Add to Defenders (host is Attackers)
        this.world.addPlayer(peerId, playerName, Team.Defenders, HeroId.Vanta);
        console.log(`[Game] Added lobby player to game: ${playerName} (${peerId}) as Defender`);
      }
    } else {
      // No existing network, create new (for single player or direct host)
      try {
        // Check if Firebase is configured
        if (FIREBASE_CONFIG.apiKey && FIREBASE_CONFIG.apiKey !== 'YOUR_API_KEY') {
          this.network = new NetworkManager({ isHost: true, useFirestore: true });
          await this.network.connectWithFirebase(FIREBASE_CONFIG);
          
          // Update local player's peerId
          const realPeerId = this.network.getLocalPeerId();
          this.localPeerId = realPeerId;
          player.peerId = realPeerId;
          this.world.updatePlayerPeerId('local', realPeerId);
          
          this.setupNetworkListeners();
          await this.network.createRoom();
          console.log(`[Game] Connected to Firebase, hosting room: ${code}`);
        } else {
          // Fallback to WebSocket signaling server
          this.network = new NetworkManager({ isHost: true });
          await this.network.connect(NETWORK.SIGNALING_SERVER);
          
          // Update local player's peerId
          const realPeerId = this.network.getLocalPeerId();
          this.localPeerId = realPeerId;
          player.peerId = realPeerId;
          this.world.updatePlayerPeerId('local', realPeerId);
          
          this.setupNetworkListeners();
          await this.network.createRoom();
          console.log(`[Game] Connected to signaling server, hosting room: ${code}`);
        }
      } catch (e) {
        console.log('[Game] Networking not available, running in local-only mode');
        this.network = null;
      }
    }

    // Check if this is a multiplayer game (we have network with actual peers connected)
    // Solo play might have a network for room code display, but no connected peers
    const connectedPeers = this.network?.getPeerCount() || 0;
    const isMultiplayerGame = this.network !== null && connectedPeers > 0;
    
    // Only spawn bots if playing solo (no connected peers)
    if (!isMultiplayerGame && this.settings.enemyBotCount > 0) {
      const botCount = this.settings.enemyBotCount;
      const difficulty = this.settings.botDifficulty;
      this.world.spawnBots(botCount, Team.Defenders, difficulty);
      console.log(`[Game] Solo mode: Spawning ${botCount} ${difficulty} enemy bots`);
    } else if (isMultiplayerGame) {
      console.log(`[Game] Multiplayer mode: No bots will be spawned (${connectedPeers} peers connected)`);
    }
    
    // Add friendly bots to player's team (Attackers) in solo mode
    if (!isMultiplayerGame && this.settings.friendlyBotCount > 0) {
      const friendlyBotCount = this.settings.friendlyBotCount;
      const difficulty = this.settings.botDifficulty;
      this.world.spawnBots(friendlyBotCount, Team.Attackers, difficulty);
      console.log(`[Game] Solo mode: Spawning ${friendlyBotCount} ${difficulty} friendly bots`);
    }

    // Start the game
    this.world.setPhase(GamePhase.RoundActive);
    this.world.startRound();
    
    // Play round start sound
    this.audio.play(SFX.RoundStart);

    // Debug: Log team composition
    const attackers = this.world.getTeamPlayers(Team.Attackers);
    const defenders = this.world.getTeamPlayers(Team.Defenders);
    console.log(`[Game] Team composition - Attackers: ${attackers.length} (${attackers.map(p => `${p.name}/${p.peerId}/${p.alive}`).join(', ')})`);
    console.log(`[Game] Team composition - Defenders: ${defenders.length} (${defenders.map(p => `${p.name}/${p.peerId}/${p.alive}`).join(', ')})`);
    
    if (defenders.length === 0 && isMultiplayerGame) {
      console.warn(`[Game] WARNING: No defenders but this is a multiplayer game! Waiting for peers...`);
    }

    console.log(`[Game] Hosting game: ${code}, hero: ${heroId}`);
    return code;
  }

  private generateRoomCode(): string {
    const letters = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // No I, O to avoid confusion
    const digits = '0123456789';
    
    let code = '';
    for (let i = 0; i < 4; i++) {
      code += letters.charAt(Math.floor(Math.random() * letters.length));
    }
    for (let i = 0; i < 2; i++) {
      code += digits.charAt(Math.floor(Math.random() * digits.length));
    }
    
    return code;
  }

  private getHeroFromSettings(): HeroId {
    switch (this.settings.playerHero) {
      case 'vanta': return HeroId.Vanta;
      case 'spectre': return HeroId.Spectre;
      case 'byte': return HeroId.Byte;
      case 'katana': return HeroId.Katana;
      case 'glyph': return HeroId.Glyph;
      case 'spark': return HeroId.Spark;
      case 'zero': return HeroId.Zero;
      case 'mira': return HeroId.Mira;
      default: return HeroId.Vanta;
    }
  }

  async joinGame(roomCode: string): Promise<void> {
    this.isHost = false;
    
    // Determine game mode from settings
    const gameMode = this.settings.gameMode === 'spike' 
      ? GameMode.DataSpikeAssault 
      : GameMode.NeonDeathmatch;
    
    // Create world as client (will receive state from host)
    this.world = new GameWorld({
      mode: gameMode,
      maxPlayers: 8,
      isHost: false,
      abilityQSlot: this.settings.abilityQSlot,
      abilityFSlot: this.settings.abilityFSlot,
      totalRounds: this.settings.totalRounds,
      mapId: this.settings.mapId,
    });
    this.gameRenderer.updateWorld(this.world);

    // Use existing network from lobby if available
    if (this.existingNetwork) {
      console.log(`[Game] Using existing network from lobby for joining`);
      this.network = this.existingNetwork;
      this.existingNetwork = null;
      this.setupNetworkListeners();
      this.localPeerId = this.network.getLocalPeerId();
    } else {
      // No existing network, create new connection
      try {
        if (FIREBASE_CONFIG.apiKey && FIREBASE_CONFIG.apiKey !== 'YOUR_API_KEY') {
          this.network = new NetworkManager({ isHost: false, useFirestore: true });
          await this.network.connectWithFirebase(FIREBASE_CONFIG);
          console.log(`[Game] Connected to Firebase for joining`);
        } else {
          this.network = new NetworkManager({ isHost: false });
          await this.network.connect(NETWORK.SIGNALING_SERVER);
          console.log(`[Game] Connected to signaling server for joining`);
        }
        
        this.setupNetworkListeners();
        
        // Join the room
        await this.network.joinRoom(roomCode);
        console.log(`[Game] Joining room: ${roomCode}`);
        
        this.localPeerId = this.network.getLocalPeerId();
      } catch (e) {
        console.error('[Game] Failed to join game:', e);
        throw new Error('Could not connect. Make sure Firebase is configured or the signaling server is running.');
      }
    }
    
    // Get hero from settings
    const heroId = this.getHeroFromSettings();
    
    // Add local player (will be synced with host)
    const player = this.world.addPlayer(
      this.localPeerId,
      this.playerName,
      Team.Defenders, // Will be assigned by host
      heroId
    );
    this.world.setLocalPlayer(player.id);
    
    // Notify host that we've joined with our info
    if (this.network) {
      this.network.broadcast({
        type: PacketType.PlayerJoin,
        timestamp: Date.now(),
        senderId: this.localPeerId,
        data: {
          name: this.playerName,
          heroId: heroId,
        },
      });
      console.log(`[Game] Sent PlayerJoin to host`);
    }
    
    // Wait for connection to host
    console.log(`[Game] Waiting for connection to host...`);
  }

  leaveGame(): void {
    this.network?.disconnect();
    this.network = null;
    this.world.setPhase(GamePhase.Lobby);
  }

  // Called when networking is set up
  protected setupNetworkListeners(): void {
    if (!this.network) return;

    this.network.on('peerConnected', ({ peerId }) => {
      console.log(`[Game] Peer connected: ${peerId}`);
      
      if (this.isHost) {
        // Check if player already exists (from lobby or previous connection)
        const existingPlayer = this.world.getPlayerByPeerId(peerId);
        if (!existingPlayer) {
          // Try to find name from lobby players
          const lobbyPlayer = this.lobbyPlayers.find(p => p.id === peerId);
          const playerName = lobbyPlayer?.name || `Player ${peerId.slice(-4)}`;
          
          // Remote players go to Defenders (host is always Attackers)
          this.world.addPlayer(peerId, playerName, Team.Defenders, HeroId.Vanta);
          console.log(`[Game] Added new player ${peerId} to Defenders: "${playerName}"`);
          
          // If this is a late-joiner (not from lobby), require PlayerJoin within 10 seconds
          if (!lobbyPlayer) {
            setTimeout(() => {
              const p = this.world.getPlayerByPeerId(peerId);
              // If player still has placeholder name, they never sent PlayerJoin - remove them
              if (p && p.name === playerName && p.name.startsWith('Player ')) {
                console.log(`[Game] Removing unverified player ${peerId} (no PlayerJoin received)`);
                this.world.removePlayer(p.id);
              }
            }, 10000);
          }
        } else {
          // Player reconnected - clear disconnected flag
          if (existingPlayer.isDisconnected) {
            existingPlayer.isDisconnected = false;
            console.log(`[Game] Player ${peerId} reconnected: ${existingPlayer.name}`);
          } else {
            console.log(`[Game] Player ${peerId} already exists: ${existingPlayer.name}`);
          }
        }
      }
    });

    this.network.on('peerDisconnected', ({ peerId }) => {
      console.log(`[Game] Peer disconnected: ${peerId}`);
      
      const player = this.world.getPlayerByPeerId(peerId);
      if (player && this.isHost) {
        // Check if this is a human player (not a bot) in a multiplayer match
        const humanPlayers = this.world.getPlayers().filter(p => !p.peerId.startsWith('bot_'));
        const remainingHumans = humanPlayers.filter(p => p.peerId !== peerId && !p.isDisconnected);
        
        console.log(`[Game] Player ${player.name} disconnected. Remaining human players: ${remainingHumans.length}`);
        
        // If the match is still active and this leaves only the host
        if (this.world.getPhase() !== GamePhase.MatchEnd && remainingHumans.length <= 1) {
          // End the match - disconnected player forfeits
          this.world.endMatchByDisconnect(player.team, player.name);
          console.log(`[Game] Match ended - ${player.name} forfeited by disconnecting`);
        } else {
          // Mark as disconnected but don't end match (might be bots remaining or multiple players)
          player.isDisconnected = true;
          console.log(`[Game] Marked player ${player.name} (${peerId}) as disconnected`);
          
          // Remove after a timeout if they don't reconnect
          setTimeout(() => {
            const p = this.world.getPlayerByPeerId(peerId);
            if (p && p.isDisconnected) {
              console.log(`[Game] Removing disconnected player ${p.name} (${peerId}) after timeout`);
              this.world.removePlayer(p.id);
            }
          }, 30000); // 30 second grace period
        }
      }
    });

    this.network.on('packet', ({ peerId, packet }) => {
      this.handlePacket(peerId, packet);
    });
    
    // Handle peers that were already connected from lobby
    // (their peerConnected event fired before we set up these listeners)
    if (this.isHost) {
      const existingPeers = this.network.getPeerIds();
      console.log(`[Game] Found ${existingPeers.length} existing peers from lobby`);
      console.log(`[Game] Lobby players:`, this.lobbyPlayers);
      
      for (const peerId of existingPeers) {
        // Remote players always go to Defenders team (opposite of host who is Attackers)
        const existingPlayer = this.world.getPlayerByPeerId(peerId);
        if (!existingPlayer) {
          // Try to find name from lobby players
          const lobbyPlayer = this.lobbyPlayers.find(p => p.id === peerId);
          const playerName = lobbyPlayer?.name || `Player ${peerId.slice(-4)}`;
          
          this.world.addPlayer(peerId, playerName, Team.Defenders, HeroId.Vanta);
          console.log(`[Game] Added remote player ${peerId} to Defenders: "${playerName}"`);
        }
      }
    }
  }

  private handlePacket(peerId: string, packet: Packet): void {
    switch (packet.type) {
      case PacketType.PlayerJoin:
        if (this.isHost) {
          // A player joined - update their info
          const data = packet.data as { name: string; heroId: HeroId };
          console.log(`[Game] Received PlayerJoin from ${peerId}: name=${data.name}, hero=${data.heroId}`);
          
          // Check if player already exists by peerId
          let player = this.world.getPlayerByPeerId(peerId);
          if (player) {
            // Update existing player info with real name and hero
            const oldName = player.name;
            player.name = data.name;
            if (data.heroId) {
              player.heroId = data.heroId;
            }
            console.log(`[Game] Updated player ${peerId}: "${oldName}" -> "${data.name}"`);
          } else {
            // Check if there's a disconnected player with the same name (reconnecting with new peerId)
            const existingByName = this.world.getPlayerByName(data.name);
            if (existingByName && existingByName.isDisconnected) {
              // This is a reconnection with a new peerId - update the old player
              const oldPeerId = existingByName.peerId;
              this.world.updatePlayerPeerId(oldPeerId, peerId);
              existingByName.isDisconnected = false;
              if (data.heroId) {
                existingByName.heroId = data.heroId;
              }
              console.log(`[Game] Reconnected player ${data.name}: ${oldPeerId} -> ${peerId}`);
            } else if (existingByName) {
              // Player with same name already exists and is connected - this is a duplicate
              // Remove the placeholder player we just added for this peerId
              const placeholderPlayer = this.world.getPlayerByPeerId(peerId);
              if (placeholderPlayer) {
                console.log(`[Game] Removing duplicate player ${peerId} (${data.name} already exists)`);
                this.world.removePlayer(placeholderPlayer.id);
              }
            } else {
              // Genuinely new player
              player = this.world.addPlayer(peerId, data.name, Team.Defenders, data.heroId || HeroId.Vanta);
              console.log(`[Game] Created player ${peerId}: "${data.name}"`);
            }
          }
        }
        break;
        
      case PacketType.Input:
        if (this.isHost) {
          // Handle input from remote player
          const inputData = packet.data as PlayerInput;
          const player = this.world.getPlayerByPeerId(peerId);
          if (player && player.alive) {
            player.setInput(inputData);
            // Debug: log interact input from remote players
            if (inputData.interact) {
              console.log(`[Game] Input from ${player.name} (${peerId}): interact=true`);
            }
            // Debug: log when input has movement
            if (inputData.moveX !== 0 || inputData.moveY !== 0) {
              console.log(`[Game] Input from ${player.name}: move(${inputData.moveX.toFixed(1)}, ${inputData.moveY.toFixed(1)})`);
            }
          } else if (!player) {
            console.log(`[Game] Input from unknown peer: ${peerId}`);
          }
        }
        break;

      case PacketType.StateSnapshot:
        if (!this.isHost) {
          // Apply state from host
          const stateData = packet.data as SerializedGameState;
          
          // Debug: log first state received
          if (!this.debugReceivedState) {
            console.log(`[Game] First state received from host - phase: ${stateData.phase}, players: ${stateData.players?.length || 0}`);
            this.debugReceivedState = true;
          }
          
          this.applySerializedState(stateData);
        }
        break;

      case PacketType.CountdownPause:
        if (this.isHost) {
          // Client requested pause
          const player = this.world.getPlayerByPeerId(peerId);
          const playerName = player?.name || 'Unknown';
          this.world.pauseCountdown(playerName);
        }
        break;

      case PacketType.CountdownResume:
        if (this.isHost) {
          // Client requested resume
          const player = this.world.getPlayerByPeerId(peerId);
          const playerName = player?.name || 'Unknown';
          this.world.resumeCountdown(playerName);
        }
        break;

      case PacketType.GamePause:
        if (this.isHost) {
          // Client lost tab focus
          const player = this.world.getPlayerByPeerId(peerId);
          const playerName = player?.name || 'Unknown';
          this.world.playerLostFocus(peerId, playerName);
        }
        break;

      case PacketType.GameResume:
        if (this.isHost) {
          // Client regained tab focus
          const player = this.world.getPlayerByPeerId(peerId);
          const playerName = player?.name || 'Unknown';
          this.world.playerRegainedFocus(peerId, playerName);
        }
        break;

      case PacketType.Event:
        // Handle game events from host (sounds, visual effects, etc.)
        if (!this.isHost) {
          const eventData = packet.data as { type: GameEventType; data: unknown };
          this.handleGameEvent(eventData);
        }
        break;
    }
  }
  
  // Convert GameState to JSON-serializable format
  private serializeState(): SerializedGameState {
    const state = this.world.getState();
    return {
      phase: state.phase,
      mode: state.mode,
      mapId: state.mapId,
      tick: state.tick,
      time: state.time,
      roundStartTime: state.roundStartTime,
      roundNumber: state.roundNumber,
      maxRounds: state.maxRounds,
      roundTimeLimit: state.roundTimeLimit,
      attackerScore: state.attackerScore,
      defenderScore: state.defenderScore,
      players: Array.from(state.players.entries()),
      projectiles: Array.from(state.projectiles.entries()),
      objectives: state.objectives,
      recentShots: state.recentShots || [],
      countdownTime: state.countdownTime ?? 10000,
      countdownPaused: state.countdownPaused ?? false,
      countdownPausedBy: state.countdownPausedBy ?? '',
      gamePaused: this.world.isGamePaused(),
      gamePausedBy: this.world.getGamePausedBy(),
      disconnectForfeit: this.world.isDisconnectForfeit(),
      disconnectedPlayerName: this.world.getDisconnectedPlayerName(),
      aircraft: state.aircraft ? Array.from(state.aircraft.entries()) : [],
      supplyCrates: state.supplyCrates ? Array.from(state.supplyCrates.entries()) : [],
      bombs: state.bombs ? Array.from(state.bombs.entries()) : [],
      grenades: state.grenades ? Array.from(state.grenades.entries()) : [],
    };
  }
  
  // Apply serialized state from network
  private applySerializedState(data: SerializedGameState): void {
    // Convert arrays back to Maps
    const state: GameState = {
      phase: data.phase,
      mode: data.mode,
      mapId: data.mapId,
      tick: data.tick,
      time: data.time,
      roundStartTime: data.roundStartTime,
      roundNumber: data.roundNumber,
      maxRounds: data.maxRounds,
      roundTimeLimit: data.roundTimeLimit,
      attackerScore: data.attackerScore,
      defenderScore: data.defenderScore,
      players: new Map(data.players),
      projectiles: new Map(data.projectiles),
      objectives: data.objectives,
      recentShots: data.recentShots || [],
      countdownTime: data.countdownTime ?? 10000,
      countdownPaused: data.countdownPaused ?? false,
      countdownPausedBy: data.countdownPausedBy ?? '',
    };
    
    // Add optional supply drop system entities if present
    if (data.aircraft && data.aircraft.length > 0) {
      state.aircraft = new Map(data.aircraft);
    }
    if (data.supplyCrates && data.supplyCrates.length > 0) {
      state.supplyCrates = new Map(data.supplyCrates);
    }
    if (data.bombs && data.bombs.length > 0) {
      state.bombs = new Map(data.bombs);
    }
    if (data.grenades && data.grenades.length > 0) {
      state.grenades = new Map(data.grenades);
    }
    
    this.world.applyState(state);
    
    // Apply game pause state from host (for clients)
    if (data.gamePaused !== undefined) {
      this.world.setGamePauseState(data.gamePaused, data.gamePausedBy ?? '');
    }
    
    // Apply disconnect forfeit state from host (for clients)
    if (data.disconnectForfeit !== undefined) {
      this.world.setDisconnectForfeitState(data.disconnectForfeit, data.disconnectedPlayerName ?? '');
    }
    
    // Add bullet tracers from host for visualization
    // Skip local player's shots - they're already shown via client-side prediction
    if (state.recentShots) {
      const localPlayer = this.world.getLocalPlayer();
      for (const shot of state.recentShots) {
        // Skip our own shots (already predicted locally)
        // Compare by peerId since entity IDs differ between host and client
        if (localPlayer && shot.shooterPeerId === localPlayer.peerId) {
          continue;
        }
        const isEnemy = this.world.isEnemyOfLocal(shot.shooterId);
        this.gameRenderer.addBulletTracer(shot.start, shot.end, isEnemy);
      }
    }
  }
  
  // Toggle countdown pause (can be called by any player)
  toggleCountdownPause(): void {
    const localPlayer = this.world.getLocalPlayer();
    const playerName = localPlayer?.name || 'Unknown';
    
    if (this.isHost) {
      // Host can toggle directly
      this.world.toggleCountdownPause(playerName);
    } else if (this.network) {
      // Client sends request to host
      const isPaused = this.world.isCountdownPaused();
      this.network.broadcast({
        type: isPaused ? PacketType.CountdownResume : PacketType.CountdownPause,
        timestamp: Date.now(),
        senderId: this.network.getLocalPeerId(),
        data: { playerName },
      });
    }
  }

  // Called when player's tab loses focus (for multiplayer pause sync)
  onTabBlur(): void {
    const localPlayer = this.world.getLocalPlayer();
    const playerName = localPlayer?.name || 'Unknown';
    const peerId = localPlayer?.peerId || this.localPeerId;
    
    if (this.isHost) {
      // Host can pause directly
      this.world.playerLostFocus(peerId, playerName);
    } else if (this.network) {
      // Client sends pause request to host
      this.network.broadcast({
        type: PacketType.GamePause,
        timestamp: Date.now(),
        senderId: this.network.getLocalPeerId(),
        data: { playerName },
      });
    }
    console.log(`[Game] Tab blur - requesting game pause for ${playerName}`);
  }

  // Called when player's tab regains focus (for multiplayer pause sync)
  onTabFocus(): void {
    const localPlayer = this.world.getLocalPlayer();
    const playerName = localPlayer?.name || 'Unknown';
    const peerId = localPlayer?.peerId || this.localPeerId;
    
    if (this.isHost) {
      // Host can resume directly
      this.world.playerRegainedFocus(peerId, playerName);
    } else if (this.network) {
      // Client sends resume request to host
      this.network.broadcast({
        type: PacketType.GameResume,
        timestamp: Date.now(),
        senderId: this.network.getLocalPeerId(),
        data: { playerName },
      });
    }
    console.log(`[Game] Tab focus - requesting game resume for ${playerName}`);
  }

  // Check if game is paused due to tab focus
  isGamePausedByFocus(): boolean {
    return this.world.isGamePaused();
  }

  getGamePausedInfo(): { isPaused: boolean; pausedBy: string; pausedCount: number } {
    return {
      isPaused: this.world.isGamePaused(),
      pausedBy: this.world.getGamePausedBy(),
      pausedCount: this.world.getPausedPlayerCount(),
    };
  }
  
  // Get current audio settings
  getAudioSettings(): ReturnType<typeof this.audio.getSettings> {
    return this.audio.getSettings();
  }
  
  // Update audio settings
  updateAudioSettings(settings: Parameters<typeof this.audio.updateSettings>[0]): void {
    this.audio.updateSettings(settings);
  }
  
  // Broadcast game state to all clients
  private broadcastState(): void {
    if (!this.network || !this.isHost) return;
    
    // Debug: log first broadcast
    if (!this.debugBroadcastStarted) {
      console.log(`[Game] Starting state broadcasts to ${this.network.getPeerCount()} peers`);
      this.debugBroadcastStarted = true;
    }
    
    const serializedState = this.serializeState();
    this.network.broadcast({
      type: PacketType.StateSnapshot,
      timestamp: Date.now(),
      senderId: this.localPeerId,
      data: serializedState,
    });
  }

  private handleGameEvent(event: { type: GameEventType; data: unknown }): void {
    switch (event.type) {
      case GameEventType.PlayerKill: {
        const data = event.data as { victimId: string; killerId: string };
        const victim = this.world.getPlayer(data.victimId);
        const localPlayer = this.world.getLocalPlayer();
        if (victim) {
          this.gameRenderer.addPlayerDeath(victim.position);
          // Play death sound for everyone
          this.audio.play(SFX.PlayerDeath);
          // Play kill confirmed only for the killer (with slight delay so it's not masked)
          if (localPlayer && data.killerId === localPlayer.id) {
            setTimeout(() => {
              this.audio.play(SFX.KillConfirmed, 1.0);
            }, 200);
          }
        }
        break;
      }
      case GameEventType.PlayerDamage: {
        const data = event.data as { targetId: string; position?: { x: number; y: number } };
        const target = this.world.getPlayer(data.targetId);
        if (target && data.position) {
          const direction = {
            x: target.position.x - data.position.x,
            y: target.position.y - data.position.y,
          };
          this.gameRenderer.addPlayerHit(data.position, direction);
          // Play damage sound
          this.audio.playWithVariation(SFX.PlayerDamage, 0.8);
        }
        break;
      }
      case GameEventType.RoundStarted: {
        // Check if this is an aircraft spawn event
        const data = event.data as { eventType?: string; pickupSound?: string };
        if (data.eventType === 'aircraft_spawn') {
          // Play airplane flyby sound
          this.audio.play(SFX.AirplaneFlyby, 0.6);
        } else if (data.eventType === 'parachute_landed') {
          // Play parachute landing sound
          this.audio.play(SFX.ParachuteDrop, 0.5);
        } else if (data.eventType === 'rocket_launch') {
          // Play rocket launch sound for entire missile strike
          this.audio.play(SFX.RocketLaunch, 0.8);
        } else if (data.eventType === 'missile_explosion') {
          // Play missile explosion sound for each missile
          this.audio.play(SFX.MissileExplosion, 0.7);
        } else if (data.eventType === 'missile_incoming') {
          // Play missile incoming sound (2 seconds before impact)
          this.audio.play(SFX.MissileIncoming, 0.7);
        } else if (data.eventType === 'crate_pickup') {
          // Show notification if it's the local player
          const localPlayer = this.world.getLocalPlayer();
          const pickupData = data as { playerId?: string; pickupSound?: string; pickupMessage?: string };
          
          if (localPlayer && pickupData.playerId === localPlayer.id && pickupData.pickupMessage) {
            // Show pickup notification with appropriate color
            let color = '#00FF88'; // Default green
            if (pickupData.pickupSound === 'weapon') {
              color = '#FF00FF'; // Magenta for weapons
            } else if (pickupData.pickupSound === 'health') {
              color = '#44FF44'; // Green for health
            } else if (pickupData.pickupMessage.includes('$')) {
              color = '#FFD700'; // Gold for credits
            }
            this.gameRenderer.addPickupNotification(pickupData.pickupMessage, color);
          }
          
          // Play appropriate pickup sound based on item type
          console.log(`[Game] Received crate_pickup event - sound type: ${pickupData.pickupSound}`);
          if (pickupData.pickupSound === 'weapon') {
            console.log('[Game] Playing WeaponPickup sound');
            this.audio.play(SFX.WeaponPickup, 0.8);
          } else if (pickupData.pickupSound === 'health') {
            console.log('[Game] Playing HealthPickup sound');
            this.audio.play(SFX.HealthPickup, 0.7);
          } else {
            console.log('[Game] Playing ItemPickup sound');
            this.audio.play(SFX.ItemPickup, 0.7);
          }
        } else if (data.eventType === 'grenade_explosion') {
          // Show explosion visual effect
          const explosionData = data as { position?: { x: number; y: number }; radius?: number };
          if (explosionData.position) {
            // Create large explosion particle effect
            this.gameRenderer.addPlayerDeath(explosionData.position);
            // Play explosion sound
            this.audio.play(SFX.Explosion, 0.9);
          }
        } else if (data.eventType === 'bomb_explosion') {
          // Show larger bomb explosion visual effect
          const explosionData = data as { position?: { x: number; y: number }; radius?: number };
          if (explosionData.position) {
            // Create extra large explosion particle effect (double the grenades)
            this.gameRenderer.addPlayerDeath(explosionData.position);
            this.gameRenderer.addPlayerDeath(explosionData.position);
            // Play explosion sound at higher volume
            this.audio.play(SFX.Explosion, 1.0);
          }
        }
        break;
      }
    }
  }

  /**
   * Play weapon shooting sound based on weapon ID
   */
  private playWeaponSound(weaponId: string): void {
    switch (weaponId) {
      case 'burst_pistol':
        this.audio.playWithVariation(SFX.PulsePistolShoot);
        break;
      case 'neo_smg9':
      case 'pulse_rifle':
        this.audio.playWithVariation(SFX.PlasmaRifleShoot);
        break;
      case 'scatter_shot':
        this.audio.playWithVariation(SFX.ScatterGunShoot);
        break;
      case 'rail_sniper':
        this.audio.playWithVariation(SFX.FusionCannonShoot);
        break;
      default:
        // Default to pulse pistol sound
        this.audio.playWithVariation(SFX.PulsePistolShoot);
    }
  }

  // -------------------------------------------------------------------------
  // Game Loop
  // -------------------------------------------------------------------------

  private update(dt: number, _time: number): void {
    // Update input
    this.inputManager.update();
    this.updateCameraInput();

    // Only process player input during active gameplay
    const currentPhase = this.world.getPhase();
    const isRoundActive = currentPhase === GamePhase.RoundActive;
    
    // Detect phase transitions for audio cues
    if (currentPhase !== this.lastPhase) {
      // Round just ended - play countdown sound synced with timer
      if (currentPhase === GamePhase.RoundEnd && this.lastPhase === GamePhase.RoundActive) {
        // Stop any existing countdown sound
        if (this.countdownSoundId) {
          this.audio.stop(this.countdownSoundId);
        }
        // Play countdown sound - assumes sound file starts immediately with "10"
        const countdownTime = this.world.getCountdownTime();
        const soundDuration = this.audio.getSoundDuration(SFX.CountdownTick);
        // Calculate offset based on remaining countdown time
        const offset = Math.max(0, soundDuration - (countdownTime / 1000));
        this.countdownSoundId = this.audio.playAtOffset(SFX.CountdownTick, offset);
        this.lastCountdownPaused = false;
      }
      // Round just started - stop countdown sound and play round start sound
      if (currentPhase === GamePhase.RoundActive && this.lastPhase === GamePhase.RoundEnd) {
        if (this.countdownSoundId) {
          this.audio.stop(this.countdownSoundId);
          this.countdownSoundId = null;
        }
        this.audio.play(SFX.RoundStart);
        
        // Reset all inputs to prevent held buttons from carrying over between rounds
        this.inputManager.resetAllInputs();
      }
      this.lastPhase = currentPhase;
    }
    
    // Handle countdown pause/resume
    if (currentPhase === GamePhase.RoundEnd) {
      const isPaused = this.world.isCountdownPaused();
      if (isPaused !== this.lastCountdownPaused) {
        if (isPaused) {
          // Pause - stop the sound
          if (this.countdownSoundId) {
            this.audio.stop(this.countdownSoundId);
            this.countdownSoundId = null;
          }
        } else {
          // Resume - restart sound from current position
          const countdownTime = this.world.getCountdownTime();
          const soundDuration = this.audio.getSoundDuration(SFX.CountdownTick);
          const offset = Math.max(0, soundDuration - (countdownTime / 1000));
          this.countdownSoundId = this.audio.playAtOffset(SFX.CountdownTick, offset);
        }
        this.lastCountdownPaused = isPaused;
      }
    }

    // Get local player input
    const localPlayer = this.world.getLocalPlayer();
    
    // Debug: log once if local player is missing
    if (!localPlayer && isRoundActive && !this.debugLoggedNoPlayer) {
      console.log(`[Game] WARNING: No local player found! isHost=${this.isHost}, localPlayerId=${this.world['localPlayerId']}`);
      this.debugLoggedNoPlayer = true;
    }
    
    if (localPlayer && isRoundActive) {
      const inputState = this.inputManager.getState();
      const playerInput: PlayerInput = {
        moveX: inputState.moveX,
        moveY: inputState.moveY,
        aimX: inputState.aimX,
        aimY: inputState.aimY,
        shoot: inputState.shoot,
        reload: inputState.reload,
        interact: inputState.interact,
        throwGrenade: inputState.throwGrenade,
        useBombCall: inputState.useBombCall,
        useMissileStrike: inputState.useMissileStrike,
        ability1: inputState.ability1,
        ability2: inputState.ability2,
        ultimate: inputState.ultimate,
        weaponSlot: inputState.weaponSlot,
        sequence: this.inputManager.getNextSequence(),
      };

      // Apply input locally
      localPlayer.setInput(playerInput);

      // Handle weapon switching (1-5 keys or mouse wheel)
      if (inputState.weaponSlot !== 0 && localPlayer.alive) {
        let switched = false;
        if (inputState.weaponSlot === -1) {
          // Mouse wheel down = next weapon
          switched = localPlayer.cycleWeaponNext();
        } else if (inputState.weaponSlot === -2) {
          // Mouse wheel up = previous weapon
          switched = localPlayer.cycleWeaponPrevious();
        } else if (inputState.weaponSlot > 0) {
          // Number key = direct slot selection
          switched = localPlayer.switchToWeaponSlot(inputState.weaponSlot);
        }
        if (switched) {
          this.gameRenderer.addWeaponSwitch(localPlayer.position);
        }
      }

      // Add muzzle flash and bullet tracer when shooting (client-side prediction)
      if (inputState.shoot && localPlayer.alive && localPlayer.canFire(this.world.getTime() + this.world.getRoundNumber() * 200000)) {
        const now = performance.now();
        if (now - this.lastLocalFireTime > 50) { // Limit flash rate
          this.gameRenderer.addMuzzleFlash(localPlayer.position, localPlayer.rotation);
          
          // Play shooting sound based on weapon
          this.playWeaponSound(localPlayer.weapon.weaponId);
          
          // Client-side predicted bullet tracer (so player sees their own shots immediately)
          if (!this.isHost) {
            // Calculate where the bullet would go with wall collision
            const weapon = localPlayer.getWeaponDefinition();
            if (weapon && weapon.projectileSpeed === 0) { // Hitscan weapon
              const direction = {
                x: Math.cos(localPlayer.rotation),
                y: Math.sin(localPlayer.rotation)
              };
              const start = {
                x: localPlayer.position.x + direction.x * 20,
                y: localPlayer.position.y + direction.y * 20
              };
              
              // Simple raycast to find wall collision - use proper collision system
              const collisionWorld = this.world.getCollisionWorld();
              const hit = collisionWorld.raycast(
                start,
                direction,
                weapon.range,
                0xFFFF // All layers (walls + players)
              );
              
              const end = hit 
                ? hit.point
                : { x: start.x + direction.x * weapon.range, y: start.y + direction.y * weapon.range };
              this.gameRenderer.addBulletTracer(start, end, false);
            }
          }
          
          this.lastLocalFireTime = now;
        }
      }

      // Send input to host
      if (!this.isHost && this.network) {
        const now = performance.now();
        if (now - this.lastInputSendTime >= 1000 / NETWORK.INPUT_RATE) {
          this.network.broadcast({
            type: PacketType.Input,
            timestamp: Date.now(),
            senderId: this.localPeerId,
            data: playerInput,
          });
          this.lastInputSendTime = now;
        }
      }
    }

    // Update world
    if (this.isHost) {
      // Host: full simulation
      this.world.update(dt);

      // Broadcast state to clients periodically (20 times per second)
      // Do this BEFORE clearing recent shots so they're included in the state
      if (this.network && this.network.getPeerCount() > 0) {
        const now = performance.now();
        if (now - this.lastStateBroadcastTime >= 50) { // 50ms = 20Hz
          this.broadcastState();
          this.lastStateBroadcastTime = now;
        }
      }

      // Process recent shots for tracer visualization (after broadcast)
      const recentShots = this.world.getAndClearRecentShots();
      for (const shot of recentShots) {
        const isEnemy = this.world.isEnemyOfLocal(shot.shooterId);
        this.gameRenderer.addBulletTracer(shot.start, shot.end, isEnemy);
      }

      // Process game events for visual effects and sounds
      const events = this.world.getPendingEvents();
      for (const event of events) {
        this.handleGameEvent(event);
        
        // Broadcast events to clients for synchronized sounds and effects
        if (this.network && this.network.getPeerCount() > 0) {
          this.network.broadcast({
            type: PacketType.Event,
            timestamp: Date.now(),
            senderId: this.localPeerId,
            data: event,
          });
        }
      }
    } else {
      // Client: update local player for client-side prediction (smooth movement)
      // The host is authoritative, but we predict locally for responsiveness
      this.world.updateLocalPlayerOnly(dt);
    }

    // Update particle system
    this.gameRenderer.updateParticles(dt);

    // Ping peers periodically
    if (this.network) {
      const now = performance.now();
      if (now - this.lastPingTime >= NETWORK.PING_INTERVAL) {
        this.network.pingPeers();
        this.lastPingTime = now;
      }
    }
  }

  private updateCameraInput(): void {
    const cameraOffset = this.renderer.getCameraOffset();
    const cameraZoom = this.renderer.getCameraZoom();
    this.inputManager.updateCamera(cameraOffset, cameraZoom);
  }

  private render(alpha: number): void {
    this.gameRenderer.render(alpha);

    // Debug/FPS info (controlled from settings)
    if (this.showFPS && this.debugInfo) {
      const fps = this.gameLoop.getFPS();
      const tps = this.gameLoop.getTPS();
      const playerCount = this.world.getPlayers().length;
      const phase = this.world.getPhase();
      const latency = this.network?.getAverageLatency() ?? 0;

      this.debugInfo.innerHTML = `
        FPS: ${fps} | TPS: ${tps}<br>
        Phase: ${phase}<br>
        Players: ${playerCount}<br>
        Tick: ${this.world.getTick()}<br>
        ${this.network ? `Latency: ${latency.toFixed(1)}ms<br>` : ''}
        ${this.isHost ? 'HOST' : 'CLIENT'}
      `;
      this.debugInfo.style.display = 'block';
    } else if (this.debugInfo) {
      this.debugInfo.style.display = 'none';
    }
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  setPlayerName(name: string): void {
    this.playerName = name;
  }

  getPlayerName(): string {
    return this.playerName;
  }

  isRunning(): boolean {
    return this.gameLoop.isRunning();
  }

  getWorld(): GameWorld {
    return this.world;
  }

  getNetwork(): NetworkManager | null {
    return this.network;
  }

  // FPS/Debug info visibility
  setShowFPS(show: boolean): void {
    this.showFPS = show;
    if (this.debugInfo && !show) {
      this.debugInfo.style.display = 'none';
    }
  }

  getShowFPS(): boolean {
    return this.showFPS;
  }

  // Hero Tutorial System
  private showHeroTutorial(heroId: string): void {
    // Check if already shown for this hero
    const shownTutorials = JSON.parse(localStorage.getItem('cybergrid_tutorials') || '[]');
    if (shownTutorials.includes(heroId)) {
      return;
    }

    const heroDef = HEROES[heroId as HeroId];
    if (!heroDef) return;

    // Create overlay
    this.heroTutorialOverlay = document.createElement('div');
    this.heroTutorialOverlay.id = 'hero-tutorial-overlay';
    this.heroTutorialOverlay.innerHTML = `
      <div class="tutorial-content">
        <h2>Playing as ${heroDef.name}</h2>
        <p class="hero-role">${heroDef.role}</p>
        
        <div class="abilities-list">
          <div class="ability-item passive">
            <span class="key">PASSIVE</span>
            <span class="name">${heroDef.abilities[0]?.name || 'Unknown'}</span>
            <span class="desc">${heroDef.abilities[0]?.description || ''}</span>
          </div>
          <div class="ability-item">
            <span class="key">Q</span>
            <span class="name">${heroDef.abilities[1]?.name || 'Unknown'}</span>
            <span class="desc">${heroDef.abilities[1]?.description || ''}</span>
          </div>
          <div class="ability-item">
            <span class="key">F</span>
            <span class="name">${heroDef.abilities[2]?.name || 'Unknown'}</span>
            <span class="desc">${heroDef.abilities[2]?.description || ''}</span>
          </div>
          <div class="ability-item ultimate">
            <span class="key">X</span>
            <span class="name">${heroDef.abilities[3]?.name || 'Unknown'}</span>
            <span class="desc">${heroDef.abilities[3]?.description || ''}</span>
          </div>
        </div>
        
        <button id="tutorial-dismiss">Got it! (Click or press any key)</button>
      </div>
    `;

    // Add styles
    const style = document.createElement('style');
    style.textContent = `
      #hero-tutorial-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.85);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
        animation: fadeIn 0.3s ease;
      }
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      .tutorial-content {
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
        border: 2px solid #0ce5f0;
        border-radius: 12px;
        padding: 30px 40px;
        max-width: 500px;
        text-align: center;
        box-shadow: 0 0 30px rgba(12, 229, 240, 0.3);
      }
      .tutorial-content h2 {
        color: #0ce5f0;
        margin: 0 0 5px 0;
        font-size: 28px;
        text-transform: uppercase;
        letter-spacing: 2px;
      }
      .hero-role {
        color: #888;
        margin: 0 0 20px 0;
        font-size: 14px;
      }
      .abilities-list {
        text-align: left;
        margin: 20px 0;
      }
      .ability-item {
        display: grid;
        grid-template-columns: 60px 1fr;
        grid-template-rows: auto auto;
        gap: 2px 10px;
        padding: 10px;
        margin: 8px 0;
        background: rgba(255,255,255,0.05);
        border-radius: 6px;
        border-left: 3px solid #0ce5f0;
      }
      .ability-item.passive {
        border-left-color: #4a4;
      }
      .ability-item.ultimate {
        border-left-color: #ffcc00;
        background: rgba(255, 200, 0, 0.1);
      }
      .ability-item .key {
        grid-row: span 2;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(12, 229, 240, 0.2);
        color: #0ce5f0;
        font-weight: bold;
        font-size: 18px;
        border-radius: 4px;
      }
      .ability-item.passive .key {
        background: rgba(68, 170, 68, 0.2);
        color: #4a4;
        font-size: 10px;
      }
      .ability-item.ultimate .key {
        background: rgba(255, 200, 0, 0.2);
        color: #ffcc00;
      }
      .ability-item .name {
        color: #fff;
        font-weight: bold;
        font-size: 14px;
      }
      .ability-item .desc {
        color: #aaa;
        font-size: 12px;
      }
      #tutorial-dismiss {
        background: linear-gradient(135deg, #0ce5f0 0%, #0891b2 100%);
        border: none;
        color: #000;
        padding: 12px 30px;
        font-size: 14px;
        font-weight: bold;
        border-radius: 6px;
        cursor: pointer;
        margin-top: 20px;
        transition: transform 0.2s, box-shadow 0.2s;
      }
      #tutorial-dismiss:hover {
        transform: scale(1.05);
        box-shadow: 0 0 20px rgba(12, 229, 240, 0.5);
      }
    `;
    document.head.appendChild(style);
    document.body.appendChild(this.heroTutorialOverlay);

    // Dismiss handler
    const dismiss = () => {
      if (this.heroTutorialOverlay) {
        this.heroTutorialOverlay.remove();
        this.heroTutorialOverlay = null;
      }
      // Save that we've shown this tutorial
      shownTutorials.push(heroId);
      localStorage.setItem('cybergrid_tutorials', JSON.stringify(shownTutorials));
      this.heroTutorialShown = true;
      
      // Remove event listeners
      document.removeEventListener('keydown', dismiss);
      document.removeEventListener('click', dismiss);
    };

    // Dismiss on click or any key
    document.getElementById('tutorial-dismiss')?.addEventListener('click', dismiss);
    setTimeout(() => {
      document.addEventListener('keydown', dismiss, { once: true });
    }, 500); // Small delay to prevent immediate dismissal
  }

  checkAndShowHeroTutorial(): void {
    if (this.heroTutorialShown) return;
    
    const heroId = this.settings.playerHero;
    this.showHeroTutorial(heroId);
  }

  // Show hero info card on demand (H key shortcut)
  showHeroInfo(): void {
    // If already showing, dismiss it
    if (this.heroTutorialOverlay) {
      this.heroTutorialOverlay.remove();
      this.heroTutorialOverlay = null;
      return;
    }

    const heroId = this.settings.playerHero;
    const heroDef = HEROES[heroId as HeroId];
    if (!heroDef) return;

    // Create overlay
    this.heroTutorialOverlay = document.createElement('div');
    this.heroTutorialOverlay.id = 'hero-tutorial-overlay';
    this.heroTutorialOverlay.innerHTML = `
      <div class="tutorial-content">
        <div class="info-columns">
          <div class="hero-column">
            <h2>${heroDef.name}</h2>
            <p class="hero-role">${heroDef.role}</p>
            <p class="hero-stats">HP: ${heroDef.health} | Shield: ${heroDef.shield} | Speed: ${Math.round(heroDef.speed * 100)}%</p>
            
            <div class="abilities-list">
              <div class="ability-item passive">
                <span class="key">PASSIVE</span>
                <span class="name">${heroDef.abilities[0]?.name || 'Unknown'}</span>
                <span class="desc">${heroDef.abilities[0]?.description || ''}</span>
              </div>
              <div class="ability-item">
                <span class="key">Q</span>
                <span class="name">${heroDef.abilities[1]?.name || 'Unknown'}</span>
                <span class="desc">${heroDef.abilities[1]?.description || ''}</span>
                <span class="cooldown">${(heroDef.abilities[1]?.cooldown || 0) / 1000}s cooldown</span>
              </div>
              <div class="ability-item">
                <span class="key">F</span>
                <span class="name">${heroDef.abilities[2]?.name || 'Unknown'}</span>
                <span class="desc">${heroDef.abilities[2]?.description || ''}</span>
                <span class="cooldown">${(heroDef.abilities[2]?.cooldown || 0) / 1000}s cooldown</span>
              </div>
              <div class="ability-item ultimate">
                <span class="key">X</span>
                <span class="name">${heroDef.abilities[3]?.name || 'Unknown'}</span>
                <span class="desc">${heroDef.abilities[3]?.description || ''}</span>
                <span class="cooldown">${heroDef.abilities[3]?.ultimateCost || 0} charge cost</span>
              </div>
            </div>
          </div>
          
          <div class="controls-column">
            <h3>Controls</h3>
            <div class="controls-list">
              <div class="control-item"><span class="ctrl-key">WASD</span><span class="ctrl-action">Move</span></div>
              <div class="control-item"><span class="ctrl-key">Mouse</span><span class="ctrl-action">Aim</span></div>
              <div class="control-item"><span class="ctrl-key">Left Click</span><span class="ctrl-action">Shoot</span></div>
              <div class="control-item"><span class="ctrl-key">Right Click</span><span class="ctrl-action">Reload</span></div>
              <div class="control-item"><span class="ctrl-key">R</span><span class="ctrl-action">Reload</span></div>
              <div class="control-item"><span class="ctrl-key">Wheel</span><span class="ctrl-action">Switch Weapon</span></div>
              <div class="control-item"><span class="ctrl-key">1-5</span><span class="ctrl-action">Select Weapon</span></div>
              <div class="control-item"><span class="ctrl-key">Q</span><span class="ctrl-action">Ability 1</span></div>
              <div class="control-item"><span class="ctrl-key">F</span><span class="ctrl-action">Ability 2</span></div>
              <div class="control-item"><span class="ctrl-key">X</span><span class="ctrl-action">Ultimate</span></div>
              <div class="control-item"><span class="ctrl-key">B</span><span class="ctrl-action">Buy Menu</span></div>
              <div class="control-item"><span class="ctrl-key">P</span><span class="ctrl-action">Pause Countdown</span></div>
              <div class="control-item"><span class="ctrl-key">ESC</span><span class="ctrl-action">Pause/Menu</span></div>
              <div class="control-item"><span class="ctrl-key">H</span><span class="ctrl-action">This Help</span></div>
            </div>
          </div>
        </div>
        
        <p class="dismiss-hint">Press H or click anywhere to close</p>
      </div>
    `;

    // Add styles if not already present
    if (!document.getElementById('hero-info-styles')) {
      const style = document.createElement('style');
      style.id = 'hero-info-styles';
      style.textContent = `
        #hero-tutorial-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.85);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          animation: fadeIn 0.2s ease;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .tutorial-content {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          border: 2px solid #0ce5f0;
          border-radius: 12px;
          padding: 30px 40px;
          max-width: 850px;
          text-align: center;
          box-shadow: 0 0 30px rgba(12, 229, 240, 0.3);
        }
        .info-columns {
          display: flex;
          gap: 30px;
          text-align: left;
        }
        .hero-column {
          flex: 1;
          min-width: 320px;
        }
        .controls-column {
          flex: 0 0 220px;
          border-left: 1px solid #333;
          padding-left: 30px;
        }
        .controls-column h3 {
          color: #0ce5f0;
          margin: 0 0 15px 0;
          font-size: 18px;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .controls-list {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .control-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 4px 0;
        }
        .ctrl-key {
          background: #333;
          color: #0ce5f0;
          padding: 3px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: bold;
          min-width: 60px;
          text-align: center;
        }
        .ctrl-action {
          color: #aaa;
          font-size: 12px;
        }
        .tutorial-content h2 {
          color: #0ce5f0;
          margin: 0 0 5px 0;
          font-size: 28px;
          text-transform: uppercase;
          letter-spacing: 2px;
        }
        .hero-role {
          color: #888;
          margin: 0 0 5px 0;
          font-style: italic;
        }
        .hero-stats {
          color: #6af;
          margin: 0 0 20px 0;
          font-size: 14px;
        }
        .abilities-list {
          text-align: left;
          margin: 20px 0;
        }
        .ability-item {
          display: grid;
          grid-template-columns: 70px 1fr;
          grid-template-rows: auto auto;
          gap: 2px 10px;
          padding: 10px;
          margin: 8px 0;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 6px;
          border-left: 3px solid #0ce5f0;
        }
        .ability-item.passive {
          border-left-color: #4a4;
          opacity: 0.8;
        }
        .ability-item.ultimate {
          border-left-color: #f0c020;
          background: rgba(240, 192, 32, 0.1);
        }
        .ability-item .key {
          grid-row: 1 / 3;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #0ce5f0;
          color: #000;
          font-weight: bold;
          font-size: 16px;
          border-radius: 4px;
          padding: 5px;
        }
        .ability-item.passive .key {
          background: #4a4;
          font-size: 10px;
        }
        .ability-item.ultimate .key {
          background: #f0c020;
        }
        .ability-item .name {
          color: #fff;
          font-weight: bold;
          font-size: 14px;
        }
        .ability-item .desc {
          color: #aaa;
          font-size: 12px;
        }
        .ability-item .cooldown {
          grid-column: 2;
          color: #0ce5f0;
          font-size: 11px;
          margin-top: 2px;
        }
        .dismiss-hint {
          color: #666;
          font-size: 12px;
          margin-top: 15px;
        }
      `;
      document.head.appendChild(style);
    }
    
    document.body.appendChild(this.heroTutorialOverlay);

    // Dismiss handler
    const dismiss = (e: Event) => {
      // Don't dismiss if clicking inside the content (except for clicking overlay background)
      if (e.type === 'click' && e.target !== this.heroTutorialOverlay) {
        return;
      }
      if (this.heroTutorialOverlay) {
        this.heroTutorialOverlay.remove();
        this.heroTutorialOverlay = null;
      }
      document.removeEventListener('click', dismiss);
    };

    // Dismiss on clicking background
    setTimeout(() => {
      this.heroTutorialOverlay?.addEventListener('click', dismiss);
    }, 100);
  }

  isHeroInfoVisible(): boolean {
    return this.heroTutorialOverlay !== null;
  }
}
